import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Stack,
  FormControl,
  FormLabel,
  Button,
  Select,
  Option,
  Alert,
} from '@mui/joy';
import { FaSave, FaUndo } from 'react-icons/fa';
import { IconWrapper } from '../../../components/IconWrapper';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import type { DocumentKind } from '../types/documents.types';

// Simple template for testing
const getDefaultTemplate = (documentType: DocumentKind) => {
  return `<h1>${documentType} Template</h1><p>This is a sample template for ${documentType}s.</p>`;
};

// Available template variables
const TEMPLATE_VARIABLES = {
  document: [
    { name: 'documentTitle', description: 'Document title' },
    { name: 'documentType', description: 'Document type' },
    { name: 'documentRef', description: 'Document reference' },
  ],
  company: [
    { name: 'companyName', description: 'Company name' },
    { name: 'companyAddress', description: 'Company address' },
  ],
};

const DocumentTemplateEditor: React.FC = () => {
  const [template, setTemplate] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentKind>('Quotation');
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string>('');

  // Load template by default when component mounts
  useEffect(() => {
    console.log('DocumentTemplateEditor mounted, setting template');
    setTemplate(getDefaultTemplate(selectedDocumentType));
  }, [selectedDocumentType]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      console.log('Saving template...');
      
      // TODO: Save template to backend
      // await templateService.saveTemplate(selectedOrganisationId, selectedDocumentType, template);
      
      setSuccess('Template saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save template');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    console.log('Resetting template');
    setTemplate(getDefaultTemplate(selectedDocumentType));
    setError(null);
    setSuccess(null);
  };

  const insertVariable = (variable: string) => {
    console.log('Inserting variable:', variable);
    setTemplate(prev => prev + `{{${variable}}}`);
  };

  const handleTabClick = (tabIndex: number) => {
    console.log('Tab clicked:', tabIndex);
    setActiveTab(tabIndex);
  };

  console.log('DocumentTemplateEditor rendering, activeTab:', activeTab);

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography level="h1" sx={{ mb: 1 }}>
          Document Template Editor
        </Typography>
        <Typography level="body-lg" sx={{ color: 'neutral.500' }}>
          Customize your PDF document templates for quotes, estimates, and invoices
        </Typography>
      </Box>

      {/* Document Type and Organization Selector */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Typography level="title-md" sx={{ mb: 2 }}>Template Settings</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 200 }}>
            <FormLabel>Document Type</FormLabel>
            <Select
              value={selectedDocumentType}
              onChange={(_, value) => setSelectedDocumentType(value || 'Quotation')}
            >
              <Option value="Quotation">Quotation</Option>
              <Option value="Estimate">Estimate</Option>
              <Option value="Invoice">Invoice</Option>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 200 }}>
            <FormLabel>Organization</FormLabel>
            <Select
              value={selectedOrganisationId}
              onChange={(_, value) => setSelectedOrganisationId(value || '')}
            >
              <Option value="">Select Organization</Option>
              <Option value="org1">Organization 1</Option>
              <Option value="org2">Organization 2</Option>
            </Select>
          </FormControl>
        </Stack>
      </Card>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert color="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Debug Section */}
      <Card sx={{ p: 2, mb: 3, border: '2px solid', borderColor: 'warning.500' }}>
        <Typography level="title-md" sx={{ color: 'warning.700' }}>Debug Information:</Typography>
        <Typography level="body-sm">Active Tab: {activeTab}</Typography>
        <Typography level="body-sm">Template Length: {template.length} characters</Typography>
        <Typography level="body-sm">Document Type: {selectedDocumentType}</Typography>
        <Typography level="body-sm">Organization ID: {selectedOrganisationId || 'None selected'}</Typography>
      </Card>

      {/* Main Editor with Simple HTML Tabs */}
      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.level1' }}>
          <Typography level="title-md">Template Editor - Select a tab below:</Typography>
        </Box>
        
        {/* Force Tabs to be visible */}
        <Box sx={{ 
          border: '3px solid', 
          borderColor: 'primary.500', 
          borderRadius: '8px',
          p: 2,
          mb: 2,
          backgroundColor: 'primary.50'
        }}>
          <Typography level="title-lg" sx={{ color: 'primary.700', mb: 2, textAlign: 'center' }}>
            🎯 TAB AREA - Tabs should be visible below this blue box
          </Typography>
        </Box>
        
        {/* Simple HTML Tabs */}
        <Box sx={{ 
          border: '3px solid', 
          borderColor: 'success.500', 
          borderRadius: '8px',
          backgroundColor: 'success.50',
          p: 2
        }}>
          <Typography level="title-lg" sx={{ color: 'success.700', mb: 2, textAlign: 'center' }}>
            🚨 SIMPLE HTML TABS - These should definitely be visible!
          </Typography>
          
          {/* Tab Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            mb: 2,
            border: '2px solid',
            borderColor: 'error.500',
            p: 1,
            backgroundColor: 'error.50'
          }}>
            <Button 
              variant={activeTab === 0 ? 'solid' : 'outlined'}
              color="primary"
              onClick={() => handleTabClick(0)}
              sx={{ 
                minHeight: '48px',
                fontSize: '1rem',
                fontWeight: 'bold',
                border: '2px solid',
                borderColor: 'primary.500'
              }}
            >
              📝 Template Editor (Tab 0)
            </Button>
            <Button 
              variant={activeTab === 1 ? 'solid' : 'outlined'}
              color="primary"
              onClick={() => handleTabClick(1)}
              sx={{ 
                minHeight: '48px',
                fontSize: '1rem',
                fontWeight: 'bold',
                border: '2px solid',
                borderColor: 'primary.500'
              }}
            >
              🔤 Available Variables (Tab 1)
            </Button>
            <Button 
              variant={activeTab === 2 ? 'solid' : 'outlined'}
              color="primary"
              onClick={() => handleTabClick(2)}
              sx={{ 
                minHeight: '48px',
                fontSize: '1rem',
                fontWeight: 'bold',
                border: '2px solid',
                borderColor: 'primary.500'
              }}
            >
              👁️ Preview (Tab 2)
            </Button>
          </Box>
          
          {/* Tab Content */}
          <Box sx={{ 
            border: '2px solid',
            borderColor: 'warning.500',
            borderRadius: '4px',
            p: 2,
            backgroundColor: 'warning.50',
            minHeight: '400px'
          }}>
            {activeTab === 0 && (
              <Box>
                <Typography level="title-md" sx={{ mb: 2, color: 'warning.700' }}>
                  📝 Template Editor Content (Tab 0)
                </Typography>
                <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.500' }}>
                  Edit your document template using HTML and Handlebars syntax. 
                  Use variables like <code>{'{{companyName}}'}</code> for dynamic content.
                </Typography>
                
                <Box sx={{ height: '300px', border: '1px solid', borderColor: 'divider' }}>
                  <TinyMCEEditor
                    tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                    licenseKey={'gpl'}
                    value={template}
                    onEditorChange={(content: string) => setTemplate(content)}
                    init={{
                      height: '100%',
                      menubar: true,
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                      ],
                      toolbar: 'undo redo | blocks | ' +
                        'bold italic forecolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat | code | help',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                      base_url: '/tinymce',
                      skin_url: '/tinymce/skins/ui/oxide',
                      theme_url: '/tinymce/themes/silver/theme.min.js'
                    }}
                  />
                </Box>
              </Box>
            )}
            
            {activeTab === 1 && (
              <Box>
                <Typography level="title-md" sx={{ mb: 2, color: 'warning.700' }}>
                  🔤 Available Template Variables (Tab 1)
                </Typography>
                <Typography level="body-sm" sx={{ mb: 3, color: 'neutral.500' }}>
                  Click on any variable to insert it into your template. Use conditional blocks like 
                  <code>{'{{#variable}}...{{/variable}}'}</code> for optional content.
                </Typography>

                <Stack spacing={3}>
                  {Object.entries(TEMPLATE_VARIABLES).map(([category, variables]) => (
                    <Box key={category}>
                      <Typography level="title-sm" sx={{ mb: 1, textTransform: 'capitalize' }}>
                        {category} Variables
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {variables.map((variable) => (
                          <Button
                            key={variable.name}
                            size="sm"
                            variant="outlined"
                            onClick={() => insertVariable(variable.name)}
                            title={variable.description}
                          >
                            {variable.name}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            
            {activeTab === 2 && (
              <Box>
                <Typography level="title-md" sx={{ mb: 2, color: 'warning.700' }}>
                  👁️ Template Preview (Tab 2)
                </Typography>
                <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.500' }}>
                  This shows how your template will look with sample data.
                </Typography>
                
                <Box 
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 'sm', 
                    p: 2,
                    height: '300px',
                    overflow: 'auto',
                    backgroundColor: 'background.surface'
                  }}
                >
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: template.replace(/\{\{(\w+)\}\}/g, '[Sample $1]') 
                    }} 
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Card>

      {/* Actions */}
      <Card sx={{ p: 2, mt: 3 }}>
        <Typography level="title-md" sx={{ mb: 2 }}>Actions</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button variant="outlined" onClick={handleReset}>
            <IconWrapper icon={FaUndo} />
            Reset to Default
          </Button>
          <Button 
            variant="solid" 
            color="primary" 
            onClick={handleSave}
            loading={saving}
          >
            <IconWrapper icon={FaSave} />
            Save Template
          </Button>
        </Stack>
      </Card>
    </Box>
  );
};

export default DocumentTemplateEditor;
